import { z } from 'zod';

/**
 * Metadata Validation Schemas
 *
 * These schemas replace unsafe `z.record(z.string(), z.any())` patterns with
 * typed, validated structures that prevent prototype pollution attacks.
 *
 * Key security features:
 * - Blocks __proto__ and constructor keys (prototype pollution prevention)
 * - Validates value types (no functions, no circular references)
 * - Enforces size limits on strings and arrays
 * - Uses .passthrough() to allow additional fields while maintaining security
 */

/**
 * Base Metadata Schema
 *
 * Allows JSON-serializable values only - NO functions, NO prototype pollution.
 * Use this for generic metadata fields where you need flexibility.
 *
 * @example
 * ```typescript
 * const schema = z.object({
 *   metadata: BaseMetadataSchema.optional(),
 * });
 *
 * // Valid
 * schema.parse({ metadata: { key: 'value', count: 42 } });
 *
 * // Rejected - prototype pollution
 * schema.parse({ metadata: { __proto__: { isAdmin: true } } });
 * ```
 */
export const BaseMetadataSchema = z
  .record(
    z.string(),
    z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.null(),
      // Allow arrays of primitives
      z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
      // Allow nested objects (one level deep) - prevents deeply nested prototype pollution
      z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
    ]),
  )
  .refine(
    (data) => {
      // Ensure no __proto__ or constructor pollution (check only own properties)
      return !Object.hasOwn(data, '__proto__') && !Object.hasOwn(data, 'constructor');
    },
    {
      message: 'Metadata cannot contain __proto__ or constructor keys (prototype pollution prevention)',
    },
  );

/**
 * Document Metadata Schema
 *
 * Specific metadata for document-related entities with known fields + extensibility.
 *
 * Security features:
 * - Validates known fields with specific constraints
 * - Uses .passthrough() to allow custom fields
 * - Blocks prototype pollution via refinement
 *
 * @example
 * ```typescript
 * // Valid
 * DocumentMetadataSchema.parse({
 *   author: 'John Doe',
 *   tags: ['contract', 'legal'],
 *   customField: 'allowed',
 * });
 *
 * // Rejected - __proto__ key
 * DocumentMetadataSchema.parse({
 *   __proto__: { isAdmin: true },
 * });
 * ```
 */
export const DocumentMetadataSchema = z
  .object({
    /**
     * Document author name
     */
    author: z.string().max(255).optional(),

    /**
     * Tags for categorization (max 50 tags, each max 100 chars)
     */
    tags: z.array(z.string().max(100)).max(50).optional(),

    /**
     * Document version identifier
     */
    version: z.string().max(50).optional(),

    /**
     * Document category
     */
    category: z.string().max(100).optional(),

    /**
     * Source of the document (URL, system name, etc.)
     */
    source: z.string().max(255).optional(),

    /**
     * Language code (ISO 639-1)
     */
    language: z.string().length(2).optional(),

    /**
     * Page count (for PDFs, Word docs, etc.)
     */
    pageCount: z.number().int().positive().optional(),

    /**
     * File size in bytes
     */
    fileSize: z.number().int().positive().optional(),
  })
  .passthrough() // Allow additional custom fields
  .refine(
    (data) => {
      // Ensure no __proto__ or constructor pollution (check only own properties)
      return !Object.hasOwn(data, '__proto__') && !Object.hasOwn(data, 'constructor');
    },
    {
      message: 'Metadata cannot contain __proto__ or constructor keys (prototype pollution prevention)',
    },
  );

/**
 * Conversation Metadata Schema
 *
 * Metadata for conversation/chat entities.
 *
 * @example
 * ```typescript
 * ConversationMetadataSchema.parse({
 *   channel: 'web',
 *   source: 'customer-portal',
 *   tags: ['support', 'urgent'],
 *   sessionId: '123e4567-e89b-12d3-a456-426614174000',
 * });
 * ```
 */
export const ConversationMetadataSchema = z
  .object({
    /**
     * Communication channel (web, mobile, api, etc.)
     */
    channel: z.string().max(100).optional(),

    /**
     * Source system or application
     */
    source: z.string().max(255).optional(),

    /**
     * Tags for categorization
     */
    tags: z.array(z.string().max(100)).max(50).optional(),

    /**
     * Session identifier for tracking
     */
    sessionId: z.string().uuid().optional(),

    /**
     * User agent string (for web/mobile channels)
     */
    userAgent: z.string().max(500).optional(),

    /**
     * IP address (for tracking/security)
     */
    ipAddress: z.string().max(45).optional(), // IPv6 max length

    /**
     * Referrer URL
     */
    referrer: z.string().url().optional(),
  })
  .passthrough()
  .refine((data) => !Object.hasOwn(data, '__proto__') && !Object.hasOwn(data, 'constructor'), {
    message: 'Metadata cannot contain __proto__ or constructor keys',
  });

/**
 * Business Signup Qualifiers Schema
 *
 * Tracks marketing attribution and signup source for businesses.
 * Replaces unsafe `z.record(z.string(), z.any())` for signup qualifiers.
 *
 * @example
 * ```typescript
 * SignupQualifiersSchema.parse({
 *   source: 'google_ads',
 *   campaign: 'Q1_2026_Enterprise',
 *   utmSource: 'google',
 *   utmMedium: 'cpc',
 *   utmCampaign: 'enterprise_trial',
 * });
 * ```
 */
export const SignupQualifiersSchema = z
  .object({
    /**
     * Signup source (organic, google_ads, partner, etc.)
     */
    source: z.string().max(255).optional(),

    /**
     * Marketing campaign identifier
     */
    campaign: z.string().max(255).optional(),

    /**
     * Referral code (if applicable)
     */
    referralCode: z.string().max(100).optional(),

    /**
     * UTM source parameter
     */
    utmSource: z.string().max(255).optional(),

    /**
     * UTM medium parameter
     */
    utmMedium: z.string().max(255).optional(),

    /**
     * UTM campaign parameter
     */
    utmCampaign: z.string().max(255).optional(),

    /**
     * UTM content parameter
     */
    utmContent: z.string().max(255).optional(),

    /**
     * UTM term parameter
     */
    utmTerm: z.string().max(255).optional(),

    /**
     * Landing page URL
     */
    landingPage: z.string().url().optional(),

    /**
     * Referrer URL
     */
    referrer: z.string().url().optional(),
  })
  .passthrough()
  .refine((data) => !Object.hasOwn(data, '__proto__') && !Object.hasOwn(data, 'constructor'), {
    message: 'Qualifiers cannot contain __proto__ or constructor keys',
  });

/**
 * TypeScript types inferred from schemas
 */
export type DocumentMetadata = z.infer<typeof DocumentMetadataSchema>;
export type ConversationMetadata = z.infer<typeof ConversationMetadataSchema>;
export type SignupQualifiers = z.infer<typeof SignupQualifiersSchema>;
export type BaseMetadata = z.infer<typeof BaseMetadataSchema>;
